import React, { createContext, useContext, useRef } from 'react';
import { usePOSConnection } from '../hooks/usePOSConnection';
import { useConnectionPolling } from '../hooks/useConnectionPolling';

const ConnectionContext = createContext(null);

/**
 * Provides connection state and handlers to child components
 * Eliminates prop drilling by making connection data available via useConnectionContext hook
 *
 * @param {Object} props
 * @param {boolean} props.businessDetailsCompleted - Whether business details are completed
 * @param {Object} props.currentShop - Current shop object
 * @param {Function} props.onShopRefresh - Shop refresh handler
 * @param {Function} props.onConnectionStateChange - Optional callback when connection state changes (for chatbot context)
 * @param {React.ReactNode} props.children - Child components
 */
export const ConnectionProvider = ({ businessDetailsCompleted, currentShop, onShopRefresh, onConnectionStateChange, children }) => {
	// Get connection state from hook
	const connectionState = usePOSConnection(businessDetailsCompleted, currentShop, onShopRefresh);

	// Notify parent of connection state changes (for chatbot context)
	const prevSnapshotRef = useRef(null);
	React.useEffect(() => {
		if (!onConnectionStateChange) return;
		const snapshot = JSON.stringify({
			stockProvider: connectionState.stock?.provider?.label,
			stockPolling: connectionState.stock?.pollingForConnection,
			productPolling: connectionState.product?.pollingForConnection
		});
		if (snapshot !== prevSnapshotRef.current) {
			prevSnapshotRef.current = snapshot;
			onConnectionStateChange(connectionState);
		}
	}, [connectionState, onConnectionStateChange]);

	// Set up connection polling
	useConnectionPolling(
		connectionState.stock.pollingForConnection,
		connectionState.product.pollingForConnection,
		currentShop,
		onShopRefresh,
		connectionState.setPollingForStockConnection,
		connectionState.setPollingForProductConnection,
		connectionState.setStockConnectionWarning,
		connectionState.setProductConnectionWarning
	);

	return <ConnectionContext.Provider value={connectionState}>{children}</ConnectionContext.Provider>;
};

/**
 * Hook to access connection context
 * Must be used within ConnectionProvider
 *
 * @returns {Object} Connection state and handlers
 * @throws {Error} If used outside ConnectionProvider
 *
 * @example
 * const { stock, product, posList, onConnectPOS } = useConnectionContext();
 *
 * // Access stock connection
 * console.log(stock.selectedPOS, stock.provider);
 *
 * // Access product connection
 * console.log(product.editing, product.formData);
 *
 * // Use handlers
 * stock.onEdit();
 * product.onFormChange('field', 'value');
 * onConnectPOS('stock');
 */
export const useConnectionContext = () => {
	const context = useContext(ConnectionContext);

	if (!context) {
		throw new Error('useConnectionContext must be used within ConnectionProvider');
	}

	return context;
};
